import { createSlice } from '@reduxjs/toolkit';

const initialState = {
    // Données d'authentification
    uid: null,
    email: null,
    numero: '',
    _id: null,
    
    // Données de profil de base
    name: null,
    doB: null,
    gender: null,
    genderSearch: null,
    LookingFor: null,
    stepCompleted: 0,
    
    // Photos
    photos: [], // Format R2: [{ photoUrl: string, objectKey: string }]
    profilePic: '',
    
    // Préférences utilisateur
    notificationOn: true,
    matchOn: true,
    online: true,
    status: 'Active',
    soundOn: true,
    
    // Données de recherche
    location: {
        coordinates: [],
        geoHash: '',
        type: 'Point'
    },
    ageSearch: [18, 60],
    
    // Statistiques utilisateur
    invitations: 5,
    lastInviteRefresh: null,
    
    // Données de compte
    role: 'normal',
    socialType: '',
    
    // Timestamps (format: timestamp en secondes)
    createdAt: Math.floor(Date.now() / 1000),
    updatedAt: Math.floor(Date.now() / 1000),
    lastTimeConnect: Math.floor(Date.now() / 1000),
    acceptedInvitations: [], // Array of timestamps when invitations were accepted
    
    // État UI
    loading: false,
    error: null,
    bio: '', // Ajout du champ bio
};

const authSlice = createSlice({
    name: 'auth',
    initialState,
    reducers: {
        setUser: (state, action) => {
            const { uid, email, numero, _id, createdAt, updatedAt, lastTimeConnect } = action.payload;
            state.uid = uid;
            state.email = email;
            state.numero = numero;
            state._id = _id;
            // Utiliser les timestamps du serveur s'ils existent, sinon créer de nouveaux
            state.createdAt = createdAt || Math.floor(Date.now() / 1000);
            state.updatedAt = updatedAt || Math.floor(Date.now() / 1000);
            state.lastTimeConnect = lastTimeConnect || Math.floor(Date.now() / 1000);
        },
        setTempPhoneNumber: (state, action) => {
            state.tempPhoneNumber = action.payload;
        },
        setPhoneNumber: (state, action) => {
            state.phoneNumber = action.payload;
            state.updatedAt = Math.floor(Date.now() / 1000);
        },
        setVerificationId: (state, action) => {
            state.verificationId = action.payload;
            state.updatedAt = Math.floor(Date.now() / 1000);
        },
        updateUserData: (state, action) => {
            return {
                ...state,
                ...action.payload,
                updatedAt: Math.floor(Date.now() / 1000)
            };
        },
        setName: (state, action) => {
            state.name = action.payload;
            state.updatedAt = Math.floor(Date.now() / 1000);
        },
        setDateOfBirth: (state, action) => {
            state.doB = action.payload;
            state.updatedAt = Math.floor(Date.now() / 1000);
        },
        setGender: (state, action) => {
            state.gender = action.payload;
            state.updatedAt = Math.floor(Date.now() / 1000);
        },
        setGenderSearch: (state, action) => {
            state.genderSearch = action.payload;
            state.updatedAt = Math.floor(Date.now() / 1000);
        },
        setLookingFor: (state, action) => {
            state.LookingFor = action.payload;
            state.updatedAt = Math.floor(Date.now() / 1000);
        },
        addPhoto: (state, action) => {
            const { secure_url, public_id, objectKey } = action.payload;
            const storageKey = objectKey || public_id;
            if (!state.photos.some(photo => (photo.objectKey || photo.public_id) === storageKey)) {
                const photoData = {
                    photoUrl: secure_url,
                    ...(objectKey ? { objectKey } : { public_id })
                };
                state.photos.push(photoData);
                // Si c'est la première photo, la définir comme photo de profil
                if (!state.profilePic && state.photos.length === 1) {
                    state.profilePic = photoData;
                }
            }
            state.updatedAt = Math.floor(Date.now() / 1000);
        },
        removePhoto: (state, action) => {
            const storageKey = action.payload;
            state.photos = state.photos.filter(photo => (photo.objectKey || photo.public_id) !== storageKey);
            // Si la photo supprimée était la photo de profil, mettre à jour profilePic
            if ((state.profilePic?.objectKey || state.profilePic?.public_id) === storageKey) {
                state.profilePic = state.photos[0] || null;
            }
            state.updatedAt = Math.floor(Date.now() / 1000);
        },
        setPhotos: (state, action) => {
            // S'assurer que chaque photo a le bon format
            const photos = action.payload.map(photo => ({
                photoUrl: photo.secure_url || photo.photoUrl,
                ...(photo.objectKey ? { objectKey: photo.objectKey } : { public_id: photo.public_id || photo.publicId })
            }));
            state.photos = photos;
            // Mettre à jour la photo de profil si nécessaire
            if (!state.profilePic && photos.length > 0) {
                state.profilePic = photos[0];
            }
            state.updatedAt = Math.floor(Date.now() / 1000);
        },
        setProfilePic: (state, action) => {
            const { secure_url, public_id } = action.payload;
            const photoData = {
                photoUrl: secure_url,
                public_id: public_id
            };
            // Vérifier que la photo existe dans la liste des photos
            if (state.photos.some(photo => photo.public_id === public_id)) {
                state.profilePic = photoData;
                state.updatedAt = Math.floor(Date.now() / 1000);
            }
        },
        setAcceptedPolicy: (state, action) => {
            state.acceptedPolicy = action.payload;
            state.updatedAt = Math.floor(Date.now() / 1000);
        },
        setUserLocation: (state, action) => {
            state.location = action.payload;
            state.updatedAt = Math.floor(Date.now() / 1000);
        },
        clearUserLocation: (state) => {
            state.location = null;
            state.updatedAt = Math.floor(Date.now() / 1000);
        },
        setLocation: (state, action) => {
            state.location = action.payload;
            state.updatedAt = Math.floor(Date.now() / 1000);
        },
        setLoading: (state, action) => {
            state.loading = action.payload;
        },
        setError: (state, action) => {
            state.error = action.payload;
        },
        setInterests: (state, action) => {
            state.interests = action.payload;
            state.updatedAt = Math.floor(Date.now() / 1000);
        },
        setAgeSearch: (state, action) => {
            state.ageSearch = action.payload;
            state.updatedAt = Math.floor(Date.now() / 1000);
        },
        updateTimestamps: (state) => {
            state.updatedAt = Math.floor(Date.now() / 1000);
            state.lastTimeConnect = Math.floor(Date.now() / 1000);
        },
        clearAuth: () => initialState,
        decreaseInvitations: (state) => {
            if (state.invitations > 0) {
                state.invitations -= 1;
            }
        },
        addWeeklyInvitations: (state) => {
            // Ajoute les invitations hebdomadaires selon le type d'abonnement
            switch (state.role) {
                case 'PLATINUM':
                    state.invitations += 5;
                    break;
                case 'GOLD':
                    state.invitations += 3;
                    break;
                default:
                    break;
            }
            state.lastInviteRefresh = new Date().toISOString();
        },
        addAcceptedInvitation(state) {
            const currentTime = Math.floor(Date.now() / 1000);
            const oneWeekAgo = currentTime - (7 * 24 * 60 * 60);
            
            // Remove timestamps older than a week
            state.acceptedInvitations ? (state.acceptedInvitations = state.acceptedInvitations.filter(
                timestamp => timestamp > oneWeekAgo
            )) :state.acceptedInvitations = [] ;
            
            // Add new timestamp
            state.acceptedInvitations.push(currentTime);
        },
        updateAuth: (state, action) => {
            // Update state with the received data
            return {
                ...state,
                ...action.payload
            };
        },
        clearAccountDeletion: (state, action) => {
            // Le serveur retire ce champ avec $unset. Une fusion Redux classique
            // ne peut donc pas supprimer sa précédente valeur persistée.
            delete state.accountDeletion;
            if (action.payload?.status) {
                state.status = action.payload.status;
            }
        },
        setBio: (state, action) => {
            state.bio = action.payload;
        },
    }
});

export const {
    setUser,
    setTempPhoneNumber,
    setPhoneNumber,
    setVerificationId,
    updateUserData,
    setName,
    setDateOfBirth,
    setGender,
    setGenderSearch,
    setLookingFor,
    addPhoto,
    removePhoto,
    setPhotos,
    setProfilePic,
    setUserLocation,
    clearUserLocation,
    setLocation,
    setLoading,
    setError,
    setInterests,
    setAgeSearch,
    updateTimestamps,
    clearAuth,
    setAcceptedPolicy,
    decreaseInvitations,
    addWeeklyInvitations,
    addAcceptedInvitation,
    updateAuth,
    clearAccountDeletion,
    setBio
} = authSlice.actions;

export default authSlice.reducer;
